import * as THREE from "three";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

/**
 * Setup
 */
const canvas = document.querySelector("canvas.webgl");
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x1a1a1a);

const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
scene.add(camera);

const renderer = new THREE.WebGLRenderer({ canvas });
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Camera setup
 */
const spherical = new THREE.Spherical(30, Math.PI / 3.5, Math.PI / 2.8);
const finalRadius = 2;
const idleTheta = Math.PI / 3.5;
const idlePhi = Math.PI / 2.8;
const target = new THREE.Vector3(0, 0, 0);

let mouseX = 0;
let mouseY = 0;
let lastInteraction = Date.now();
let autoRotate = false;
let resetting = false;
let introProgress = 0;
let introDone = false;

const maxThetaOffset = THREE.MathUtils.degToRad(70);
const maxPhiOffset = THREE.MathUtils.degToRad(20);

/**
 * Interaction
 */
const updateMouse = (x, y) => {
  mouseX = (x / window.innerWidth - 0.5) * 2;
  mouseY = (y / window.innerHeight - 0.5) * 2;
  lastInteraction = Date.now();

  if (autoRotate) {
    autoRotate = false;
    resetting = true;
  }
};

window.addEventListener("pointermove", (e) =>
  updateMouse(e.clientX, e.clientY)
);
window.addEventListener("deviceorientation", (e) => {
  if (e.gamma !== null && e.beta !== null) {
    updateMouse(e.gamma / 45, (e.beta - 90) / 45);
  }
});

/**
 * Resize
 */
window.addEventListener("resize", () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();
  renderer.setSize(sizes.width, sizes.height);
});

/**
 * Materials
 */
const textureLoader = new THREE.TextureLoader();
const matcapTexture = textureLoader.load("/textures/matcaps/7.png");
matcapTexture.colorSpace = THREE.SRGBColorSpace;
const matcapMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture });

const donutTexture = textureLoader.load("/textures/matcaps/3.png");
donutTexture.colorSpace = THREE.SRGBColorSpace;
const donutMaterial = new THREE.MeshMatcapMaterial({ matcap: donutTexture });

/**
 * Scene Geometry
 */
const donutGeometry = new THREE.TorusGeometry(0.3, 0.2, 20, 45);
const exclusionRadius = 1.5;

const fontLoader = new FontLoader();
fontLoader.load("/helvetiker_regular.typeface.json", (font) => {
  const textGeometry = new TextGeometry("brat", {
    font,
    size: 0.5,
    height: 0.2,
    curveSegments: 5,
    bevelEnabled: true,
    bevelThickness: 0.03,
    bevelSize: 0.02,
    bevelOffset: 0,
    bevelSegments: 4,
    depth: 0.1,
  });

  textGeometry.center();
  const text = new THREE.Mesh(textGeometry, matcapMaterial);
  scene.add(text);

  for (let i = 0; i < 300; i++) {
    let position;
    do {
      position = new THREE.Vector3(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10
      );
    } while (position.length() < exclusionRadius);

    const donut = new THREE.Mesh(donutGeometry, donutMaterial);
    donut.position.copy(position);
    donut.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
    const scale = Math.random();
    donut.scale.set(scale, scale, scale);
    scene.add(donut);
  }
});

/**
 * Animation Loop
 */
const clock = new THREE.Clock();

const animate = () => {
  const elapsed = clock.getElapsedTime();
  const now = Date.now();
  const idle = now - lastInteraction > 3000;

  if (!introDone) {
    introProgress += 0.01;
    const eased = 1 - Math.pow(1 - introProgress, 4);
    spherical.radius = THREE.MathUtils.lerp(30, finalRadius, eased);
    if (introProgress >= 1) {
      introDone = true;
    }
  } else if (idle && !resetting) {
    autoRotate = true;
  }

  if (autoRotate) {
    spherical.theta += 0.002;
  } else {
    const targetTheta = idleTheta + mouseX * maxThetaOffset;
    const targetPhi = idlePhi - mouseY * maxPhiOffset;

    if (resetting) {
      spherical.theta += (targetTheta - spherical.theta) * 0.05;
      spherical.phi += (targetPhi - spherical.phi) * 0.05;

      const diffTheta = Math.abs(spherical.theta - targetTheta);
      const diffPhi = Math.abs(spherical.phi - targetPhi);
      if (diffTheta + diffPhi < 0.01) {
        resetting = false;
      }
    } else {
      spherical.theta += (targetTheta - spherical.theta) * 0.1;
      spherical.phi += (targetPhi - spherical.phi) * 0.1;
    }
  }

  spherical.makeSafe();
  camera.position.setFromSpherical(spherical);
  camera.lookAt(target);

  renderer.render(scene, camera);
  requestAnimationFrame(animate);
};

animate();
